/**
 * Notifications.
 *
 * Polls `/api/notifications/feed` every 30 seconds while the tab is visible
 * (spec §12). Mark-read and mark-all-read mutations write through, then
 * invalidate the feed so the bell count updates immediately.
 *
 * The wire shape mirrors `NotificationFeedResponseSchema` from
 * `packages/shared/src/schemas/notifications.ts`, inlined here so the web
 * app doesn't depend on the TypeBox runtime.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, type ApiError } from './api-client';

export type NotificationType =
  | 'ENTITY_ENTERED_STAGE'
  | 'ITEM_OVERDUE'
  | 'INVOICE_UNLOCKED'
  | 'INSPECTION_NON_ACCEPTED'
  | 'PARTS_SUBMISSION_PENDING'
  | 'PARTS_SUBMISSION_REJECTED'
  | 'WARRANTY_END_APPROACHING'
  | 'BATCH_CANCELLED'
  | 'BATCH_REPLACED';

export interface NotificationEvent {
  id: string;
  notificationType: NotificationType;
  projectId: string | null;
  projectItemId: string | null;
  orderBatchId: string | null;
  title: string;
  body: string;
  /** JSON-encoded metadata; parse only when needed. */
  metadataJson: string | null;
  createdAt: string;
  /** ISO timestamp when the current user marked this read. Null = unread. */
  readAt: string | null;
}

export interface NotificationFeedResponse {
  items: NotificationEvent[];
  unreadCount: number;
}

export interface NotificationFeedQuery {
  unreadOnly?: boolean;
  limit?: number;
}

// ---------------------------------------------------------------------------
// Polling discipline (spec §12 — "Polled every 30s when tab focused")
// ---------------------------------------------------------------------------

const FEED_POLL_INTERVAL_MS = 30_000;

export const notificationFeedKey = (q: NotificationFeedQuery) =>
  ['notifications', 'feed', q] as const;

/**
 * Subscribe to the notification feed. Refetches every 30 seconds while the
 * tab is visible. React Query's `refetchInterval` callback receives the
 * Query object; we use `refetchIntervalInBackground: false` so the poll
 * pauses on hidden tabs (matches spec — no point polling when no one's
 * looking, and it saves battery on mobile).
 */
export function useNotificationFeed(query: NotificationFeedQuery = {}) {
  return useQuery({
    queryKey: notificationFeedKey(query),
    queryFn: ({ signal }) =>
      apiRequest<NotificationFeedResponse>('/api/notifications/feed', {
        query: {
          unreadOnly: query.unreadOnly,
          limit: query.limit,
        },
        signal,
      }),
    refetchInterval: FEED_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    // Smooth the unread-count display so it doesn't drop to "—" between polls.
    placeholderData: (prev) => prev,
  });
}

export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<{ markedCount: number }, ApiError, { notificationEventIds: string[] }>({
    mutationFn: (body) =>
      apiRequest<{ markedCount: number }>('/api/notifications/mark-read', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<{ markedCount: number }, ApiError, void>({
    mutationFn: () =>
      apiRequest<{ markedCount: number }>('/api/notifications/mark-all-read', {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
